<template>
  <div v-if="carouselList.length">
    <ClientOnly>
      <div class="relative">
        <swiper-container
          ref="containerRef"
          :slides-per-view="contextSlidesPerView"
          :slides-per-group="slidesPerGroup"
        >
          <swiper-slide
            v-for="item in carouselList"
            :key="item.name"
            class="h-auto p-2.5"
          >
            <slot name="card" :item="item" />
          </swiper-slide>
        </swiper-container>

        <button
          v-if="isShowButton"
          class="absolute left-0 top-2/4 z-50 flex h-16 w-16 -translate-x-20 -translate-y-2/4 items-center justify-center rounded-full bg-gray-600_a15 text-black transition-all hover:bg-gray-600_a25"
          :class="isBeginning && 'pointer-events-none opacity-50'"
          @click="prev()"
        >
          <IconCaret direction="left" />
        </button>

        <button
          v-if="isShowButton"
          class="absolute right-0 top-2/4 z-50 flex h-16 w-16 -translate-y-2/4 translate-x-20 items-center justify-center rounded-full bg-gray-600_a15 text-black transition-all hover:bg-gray-600_a25"
          :class="isEnd && 'pointer-events-none opacity-50'"
          @click="next()"
        >
          <IconCaret direction="right" />
        </button>
      </div>
    </ClientOnly>

    <ul class="hidden">
      <li v-for="item in carouselList" :key="item.name">
        <slot name="card" :item="item" />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts" generic="T extends { name: string }">
  interface IProps {
    carouselList?: T[];
    slidesPerView?: number;
  }

  const props = withDefaults(defineProps<IProps>(), {
    carouselList: () => [],
    slidesPerView: 4,
  });

  const { isDesktop, isTablet } = useScreen();

  const containerRef = ref<null>(null);

  const { instance, isBeginning, isEnd, next, prev } = useSwiper(containerRef, {
    loop: !isDesktop.value,
    pagination: {
      enabled: isDesktop.value,
      clickable: true,
    },
  });

  const isShowButton = computed((): boolean => {
    if (isDesktop.value) {
      return props.carouselList.length > contextSlidesPerView.value;
    }
    return false;
  });

  const contextSlidesPerView = computed((): number => {
    if (isDesktop.value) {
      return props.slidesPerView;
    } else if (isTablet.value) {
      return 2;
    } else {
      return 1.2;
    }
  });

  const slidesPerGroup = computed((): number => {
    if (isTablet.value) {
      return 2;
    } else {
      return 1;
    }
  });

  watch(
    () => props.carouselList,
    async () => {
      await nextTick();
      if (!instance.value) return;
      instance.value.updateSlides();
      instance.value.slideTo(0, 0);
      instance.value.updateSlidesClasses();
    }
  );
</script>

<style lang="scss" scoped>
  swiper-container {
    &::part(pagination) {
      @apply hidden;
    }
  }
</style>
